<svelte:options
  customElement={{
    tag: "oc-toggletip-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      position: { type: "String" },
      visible: { type: "Boolean", reflect: true },
      sticky: { type: "Boolean", reflect: true },
      headline: { type: "String" },
      iconType: { type: "String", attribute: "icon-type" },
      headlineLevel: { type: "Number", attribute: "headline-level" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import type { Events, Props } from "./ToggletipV1.types";
  import type { BetaProps } from "./ToggletipV1.types.beta";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import Popover from "../../../common/components/Popover/Popover.svelte";
  import { PopoverPosition } from "../../../common/components/Popover/Popover.types";
  import Bubble from "../../../common/components/Popover/Bubble.svelte";

  let {
    position,
    visible = false,
    sticky = false,
    headline,
    iconType,
    headlineLevel = undefined,
  }: Props & BetaProps = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);

  const slots = useSlots(Host);
  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  let contextElement = $state<HTMLElement>();
  let closeButton = $state<HTMLElement>();
  let popoverElement = $state<Popover>();

  /*                                                                     */
  let focusReturn: HTMLElement | null = null;

  let moveFocusToButton = false;

  function open(target: EventTarget | null, manageFocus: boolean): void {
    if (visible) return;
    if (!dispatch("oc-toggletip-open", undefined, { alias: ["oc-open"] })) return;
    visible = true;

    if (!manageFocus) return;
    if (target instanceof HTMLElement) focusReturn = target;
    /*                                             */
    moveFocusToButton = true;
  }

  function handleFullyShown(): void {
    if (moveFocusToButton && closeButton) {
      closeButton.focus();
      moveFocusToButton = false;
    }
  }

  function close(manageFocus: boolean): void {
    if (!visible) return;
    if (!dispatch("oc-toggletip-close", undefined, { alias: ["oc-close"] })) return;
    visible = false;
    moveFocusToButton = false;

    if (manageFocus) focusReturn?.focus();
    focusReturn = null;
  }

  function onToggleClick(evt: MouseEvent): void {
    const manageFocus = evt.detail === 0;
    if (visible) return close(manageFocus);
    open(evt.composedPath()[0], manageFocus);
  }

  function onCloseClick(evt: MouseEvent): void {
    close(evt.detail === 0);
  }

  function onContentKeyup(evt: KeyboardEvent): void {
    if (evt.key === "Escape") close(true);
  }

  let closeOnClick = true;

  function onBoxClick(): void {
    closeOnClick = false;
  }

  /*                                   */
  const onDocumentClick = (): void => {
    if (closeOnClick) close(false);
    closeOnClick = true;
  };
</script>

<svelte:window onresize={() => close(true)} />
<svelte:document onclick={onDocumentClick} />
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="toggletip-box" onclick={onBoxClick}>
  <div class="activator" onclick={onToggleClick} bind:this={contextElement}>
    <slot />
  </div>
  <Popover
    show={visible}
    {sticky}
    preferredPosition={position as PopoverPosition}
    onFullyShown={handleFullyShown}
    bind:this={popoverElement}
    {contextElement}
    bubble={Bubble}
  >
    <div
      role="dialog"
      tabindex="0"
      aria-labelledby={headline ? "toggletip-headline" : "toggletip-content"}
      aria-describedby={headline ? "toggletip-content" : undefined}
      onkeyup={onContentKeyup}
    >
      <div class="main">
        {#if iconType}
          <div class="icon-wrapper"><oc-icon-v1 type={iconType} color="#fff"></oc-icon-v1></div>
        {/if}
        <div class="inline-wrapper">
          <span class="close-button-wrapper">
            <oc-icon-button-v3
              class="close-button"
              variant="transparent-inverted"
              icon="close"
              oc-aria-label="Schließen"
              bind:this={closeButton}
              onclick={onCloseClick}
            ></oc-icon-button-v3></span
          >
          <span class="content">
            {#if headline}
              <svelte:element this={headlineElement} id="toggletip-headline" class="headline">
                {headline}
              </svelte:element>
            {/if}
            <slot name="toggletip-content" id="toggletip-content" />
          </span>
        </div>
      </div>
      {#if slots["toggletip-actions"]}
        <div class="actions">
          <slot name="toggletip-actions" />
        </div>
      {/if}
    </div>
  </Popover>
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      display: inline-block;

      /*                                                             */
      --popover-frame-spacing: #{tokens.$oc-component-toggletip-frame-spacing};
      --popover-max-width: #{tokens.$oc-component-toggletip-max-width};
      --popover-transition-show:
        opacity #{tokens.$oc-component-toggletip-show-hide-transition-duration}
          #{tokens.$oc-component-toggletip-show-hide-transition-easing} 0.05s,
        visibility 0ms #{tokens.$oc-component-toggletip-show-hide-transition-duration};
      --popover-transition-hide:
        opacity #{tokens.$oc-component-toggletip-show-hide-transition-duration}
          #{tokens.$oc-component-toggletip-show-hide-transition-easing} 0s,
        visibility 0ms #{tokens.$oc-component-toggletip-show-hide-transition-duration};

      /*                                      */
      --bubble-background-color: #{tokens.$oc-component-toggletip-background-color};
      --bubble-copy-color: #{tokens.$oc-component-toggletip-copy-color};
      --bubble-border-radius: #{tokens.$oc-component-toggletip-border-radius};
      --bubble-padding: #{tokens.$oc-component-toggletip-close-button-spacing} 8px
        #{tokens.$oc-component-toggletip-action-spacing-bottom}
        #{tokens.$oc-component-toggletip-text-spacing};
      --bubble-copy-font: #{tokens.$oc-component-toggletip-copy-font};
      --bubble-arrow-width: #{tokens.$oc-component-toggletip-arrow-width};
      --bubble-arrow-height: #{tokens.$oc-component-toggletip-arrow-height};
      --bubble-context-spacing: #{tokens.$oc-component-toggletip-context-spacing};
    }

    .toggletip-box {
      display: inline-block;
      max-width: 100%;
      vertical-align: bottom; /*                                                                  */
    }

    .main {
      display: flex;
    }

    .icon-wrapper {
      padding-top: tokens.$oc-component-toggletip-gap-y;
      padding-right: tokens.$oc-component-toggletip-gap-x;
    }

    .inline-wrapper {
      display: inline-block;
      width: max-content;
      max-width: 100%;
      padding: tokens.$oc-component-toggletip-gap-y 0 tokens.$oc-component-toggletip-gap-y;
      hyphens: auto;
      overflow-wrap: break-word;
    }

    .headline {
      font: tokens.$oc-component-toggletip-headline-font;
      margin-top: 0;
      margin-bottom: tokens.$oc-component-toggletip-gap-y;
    }

    .content {
      float: none;
    }

    .actions {
      text-align: right;
    }

    .close-button-wrapper {
      min-height: calc(1lh + 2 * tokens.$oc-component-toggletip-gap-y);
      display: inline-flex;
      align-items: center;
      float: right;
      margin: calc(-1 * tokens.$oc-component-toggletip-gap-y) 0
        calc(-1 * tokens.$oc-component-toggletip-gap-y) tokens.$oc-component-toggletip-gap-x;
    }
  }
</style>
